import { weekendDaysInLocalPickerRange } from './absence-report-dialog.weekend';

/** Inputs used by the absence date-range validation rules. */
export interface AbsenceDateRangeValidationInput {
  dateFrom: Date | null;
  dateTo: Date | null;
  durationHours: number | null;
  includeSaturday: boolean | null;
  includeSunday: boolean | null;
}

/** Stable error kinds surfaced to the dialog UI and tests. */
export type AbsenceDateRangeErrorKind =
  'fromRequired' | 'toRequired' | 'invalidDateRange' | 'durationInvalid' | 'unknownSaturday' | 'unknownSunday';

/**
 * Validates the absence date range, duration and weekend flags.
 * Pure function shared by Signal Forms schema and unit tests.
 */
export function validateAbsenceDateRange(
  input: AbsenceDateRangeValidationInput
): AbsenceDateRangeErrorKind | undefined {
  const { dateFrom, dateTo, durationHours, includeSaturday, includeSunday } = input;
  const hasDuration = typeof durationHours === 'number' && durationHours > 0;

  if (!dateFrom) {
    return 'fromRequired';
  }

  if (!dateTo) {
    return 'toRequired';
  }

  // Inclusive range: From == To is exactly one day; only To before From is invalid.
  const fromDay = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate()).getTime();
  const toDay = new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate()).getTime();
  if (toDay < fromDay) {
    return 'invalidDateRange';
  }

  if (!hasDuration) {
    return 'durationInvalid';
  }

  const weekends = weekendDaysInLocalPickerRange(dateFrom, dateTo);
  if (weekends.saturday && includeSaturday === null) {
    return 'unknownSaturday';
  }
  if (weekends.sunday && includeSunday === null) {
    return 'unknownSunday';
  }

  return undefined;
}

export function toIsoDateTime(value: Date | null | undefined): string | undefined {
  return value ? value.toISOString() : undefined;
}
